import React from 'react';
import PropTypes from 'prop-types';
import {RichText} from 'prismic-reactjs';

import {Box, Container, Flex, Grid, GridItem, Heading} from '@spothero/ui/';

import {
    bodySerializer,
    getSliceThemeVariables,
    ignoreEmptyRichTextObjects,
} from 'cms/utils';
import Image from 'common/image';
import ExtendedButton from 'cms/components/extended-button';
import {SLICE_PROP_TYPES, IMAGE_PROP_TYPES} from 'cms/proptypes';

const FeaturedImages = ({slice}) => {
    const {
        primary: {
            featured_images_title: featuredTitle,
            number_of_columns: numberOfColumns,
            section_id: sectionId,
            slice_theme: sliceTheme,
        },
        items: columns,
    } = slice;
    const {textColor, backgroundColor} = getSliceThemeVariables(sliceTheme);

    const colSpan = new Map([
        ['4', {base: '12 span', tablet: '6 span', desktop: '3 span'}],
        ['3', {base: '12 span', tablet: '4 span'}],
        ['2', {base: '12 span', tablet: '6 span'}],
    ]);

    return (
        <Box
            as="section"
            id={sectionId}
            color={textColor}
            backgroundColor={backgroundColor}
            paddingTop={{base: 8, tablet: 16}}
            paddingBottom={{base: 8, tablet: 16}}
        >
            <Container>
                <Grid>
                    {featuredTitle && (
                        <GridItem
                            colStart={{base: 0, tablet: 3}}
                            colSpan={{base: 12, tablet: 8}}
                        >
                            <Heading
                                as="h2"
                                textAlign="center"
                                marginBottom={{base: 6, tablet: 8}}
                            >
                                {featuredTitle}
                            </Heading>
                        </GridItem>
                    )}
                    {columns.map((column, i) => (
                        <GridItem
                            key={i}
                            gridColumn={colSpan.get(numberOfColumns)}
                            marginBottom={{base: 6, tablet: 8}}
                        >
                            <Flex
                                display={{base: 'flex'}}
                                flexDirection="column"
                                alignItems="center"
                                textAlign="center"
                            >
                                {column?.image?.url && (
                                    <Box
                                        maxHeight={{
                                            base: '25rem',
                                            tablet: '37.5rem',
                                        }}
                                        marginBottom={4}
                                    >
                                        <Image
                                            src={column.image.url}
                                            alt={column.image?.alt}
                                            lazyLoad
                                            showLoader={false}
                                        />
                                    </Box>
                                )}
                                <Box
                                    flex="1"
                                    marginBottom={2}
                                    textAlign={{base: 'left', tablet: 'center'}}
                                >
                                    {column?.column_title && (
                                        <Heading
                                            as="h3"
                                            marginBottom={4}
                                            textAlign="center"
                                        >
                                            {column.column_title}
                                        </Heading>
                                    )}
                                    {column?.column_paragraph && (
                                        <RichText
                                            render={ignoreEmptyRichTextObjects(
                                                column.column_paragraph
                                            )}
                                            htmlSerializer={bodySerializer()}
                                        />
                                    )}
                                </Box>
                                {column?.cta_text && (
                                    <ExtendedButton
                                        buttonText={column.cta_text}
                                        inPageAnchor={column.in_page_anchor}
                                        link={column.cta_external_link.url}
                                        linkTarget={
                                            column.cta_external_link?.target
                                        }
                                        minWidth={40}
                                    />
                                )}
                            </Flex>
                        </GridItem>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

/* eslint-disable camelcase */
FeaturedImages.propTypes = {
    slice: PropTypes.shape({
        ...SLICE_PROP_TYPES,
        primary: PropTypes.shape({
            section_id: PropTypes.string,
            featured_images_title: PropTypes.string,
            number_of_columns: PropTypes.string.isRequired,
            slice_theme: PropTypes.string.isRequired,
        }).isRequired,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                image: IMAGE_PROP_TYPES,
                column_title: PropTypes.string,
                column_paragraph: PropTypes.array,
                cta_text: PropTypes.string,
                in_page_anchor: PropTypes.string,
                cta_external_link: PropTypes.shape({
                    url: PropTypes.string,
                    target: PropTypes.string,
                }),
            })
        ).isRequired,
    }).isRequired,
};
/* eslint-enable camelcase */

export default FeaturedImages;
